import FormField from './FormField'

export default class FormData {
  constructor (id, formDescriptorId) {
    this.id = id
    this.formDescriptorId = formDescriptorId
    this.fields = {}
    this.dependencies = {}
    this.validations = {}
    this.callbacks = {}
  }

  static asFormData (json) {
    let formData = new FormData(
      json.id,
      json.dynamic_form_id
    )

    /* Save already existing FormField data */
    json.data_objects.forEach(obj => {
      formData.fields[obj.uid] = {
        value: obj.value,
        data_group: obj.data_group
      }
    })
    return formData
  }

  static initiateFormDatas (formDescriptors) {
    /* Create FormData for all FormDescriptors */
    let formDatas = []
    formDescriptors.forEach(formDescriptor => {
      if (!formDatas.find(formData => formDescriptor.id === formData.formDescriptorId)) {
        let formData = new FormData(null, formDescriptor.id)
        formData.initiateFields(formDescriptor.fieldSets)
        formData.initiateDependencies(formDescriptor.fieldSets)
        formData.initiateValidations(formDescriptor.fieldSets)
        formDatas.push(formData)
      }
    })
    return formDatas
  }

  static loadInitialFormDatas (initalFormData, formDatas) {
    /* Combine data group and uid to find the form data */
    if (initalFormData) {
      initalFormData.forEach(dataGroups => {
        dataGroups.forEach((dataGroup) => {
          let groupId = dataGroup.data_group
          dataGroup.data_objects.forEach(dataObject => {
            let dgUid = FormField.createFieldId({
              data_group: groupId,
              uid: dataObject.uid
            }, 0) // use a random number, 0 or anything will work
            // for each formData insert any matching data from the initalFormData
            formDatas.forEach(formData => {
              for (let key in formData.fields) {
                if (key.replace(/[0-9]/g, '') === dgUid.replace(/[0-9]/g, '')) { // compare UIDs without considering the index
                  let formField = formData.fields[key]
                  formField.value = FormField.parseValue(dataObject.value, formField.type, formField.properties)
                  formField.file = dataObject.file && dataObject.file
                  formField.files = dataObject.files && dataObject.files
                }
              }
            })
          })
        })
      })
    }
  }

  static loadInitialFormDatasWithIndex (initalFormData, formDatas) {
    /* Combine data group and uid to find the form data */
    if (initalFormData) {
      initalFormData.forEach(dataGroups => {
        dataGroups.forEach((dataGroup, index) => {
          let groupId = dataGroup.data_group
          dataGroup.data_objects.forEach(dataObject => {
            let dgUid = FormField.createFieldId({
              data_group: groupId,
              uid: dataObject.uid
            }, index)
            // for each formData insert any matching data from the initalFormData
            formDatas.forEach(formData => {
              let formField = formData.fields[dgUid]
              if (formField) {
                formField.value = FormField.parseValue(dataObject.value, formField.type, formField.properties)
                formField.file = dataObject.file && dataObject.file
                formField.files = dataObject.files && dataObject.files
              }
            })
          })
        })
      })
    }
  }

  initiateFields (fieldSets) {
    for (let [index, fieldSet] of fieldSets.entries()) {
      if (!fieldSet.fields || fieldSet.fields.length < 1) {
        continue
      }
      for (let formField of fieldSet.fields) {
        let fieldData = formField.createFormFieldData(index)
        this.fields[fieldData.id] = fieldData
      }
    }
  }

  initiateDependencies (fieldSets) {
    for (let [index, fieldSet] of fieldSets.entries()) {
      if (fieldSet.dependencies && fieldSet.dependencies.length > 0) {
        for (let dep of fieldSet.dependencies) {
          let dgUid = FormField.createFieldId(dep.dependent_form_field, index)
          if (!this.dependencies[dgUid]) {
            this.dependencies[dgUid] = []
          }
          this.dependencies[dgUid].push(fieldSet)
        }
      }
    }
  }

  initiateValidations (fieldSets) {
    for (let fieldSet of fieldSets) {
      if (!fieldSet.fields || fieldSet.fields.length < 1) {
        continue
      }

      for (let field of fieldSet.fields) {
        this.validations[field.id] = {
          is_valid: true,
          is_dependencies_satisfied: fieldSet.isDependenciesSatisfied,
          properties: field.properties,
          message: ''
        }
      }
    }
  }

  removeFieldForId (id) {
    delete this.fields[id]
    delete this.validations[id]
  }

  /* Uses multiple form field datas */
  static getFieldForUid (formDatas, uid) {
    for (let formData of formDatas) {
      for (let field in formData.fields) {
        if (formData.fields[field].uid === uid) {
          return formData.fields[field]
        }
      }
    }
    return null
  }

  /* Uses the instance form field data */
  getFieldForUid (uid) {
    for (let field in this.fields) {
      if (this.fields[field].uid === uid) {
        return this.fields[field]
      }
    }
    return null
  }

  static addCallback (formData, field, callback) {
    formData.callbacks[field.id] = callback
  }

  static validateFormDatas (formDatas) {
    let valid = true

    for (let formData of formDatas) {
      if (!formData.validate()) {
        valid = false
      }
    }

    return valid
  }

  validate () {
    let valid = true

    for (let id in this.validations) {
      this.fields[id].isDependencySatisfied = true
      if (!this.validations[id].is_dependencies_satisfied) {
        this.fields[id].isDependencySatisfied = false
        continue
      }
      if (!FormField.validateValue(this.fields[id].value, this.fields[id].type, this.validations[id])) {
        valid = false
      }
    }

    return valid
  }

  handleInvalidResponse (response) {
    for (let data of response) {
      let dgUid = FormField.createFieldId(data, 0) // use a random number, 0 or anything will work
      for (let key in this.validations) {
        if (key.replace(/[0-9]/g, '') === dgUid.replace(/[0-9]/g, '')) { // compare UIDs without considering the index
          this.validations[key].is_valid = false
          this.validations[key].message = data.message
        }
      }
    }
  }

  static serializeFormFiles (formFiles, fieldData) {
    let files = []
    for (let file of fieldData.value) {
      if (!(file instanceof Blob)) {
        continue
      }
      files.push(file)
    }
    formFiles[fieldData.id] = files
    // Set value to id of file so it can be matched with correctly
    fieldData.value = fieldData.id
  }

  static serializeFormDatas (formDatas) {
    let validData = []
    // Validate the data
    formDatas.forEach(formData => {
      let valid = formData.validate()
      if (valid) {
        validData.push(formData.fields)
      }
    })
    // Add values to data objects skipping empty fields
    let dataGroups = []
    let formFiles = {}
    validData.forEach(formData => {
      for (let fieldId in formData) {
        let fieldData = formData[fieldId]
        let value = fieldData.value
        if (value === null || (Array.isArray(value) && !value.length) || !fieldData.isDependencySatisfied) {
          continue
        }
        let fieldDataCopy = fieldData.copy()
        if (FormField.isFileField(fieldData.type)) {
          FormData.serializeFormFiles(formFiles, fieldDataCopy)
        }

        let dgi = fieldData.dataGroup
        let sdgi = fieldData.dataSubGroup
        let group = dataGroups.find(group => {
          return group.key === dgi && group.skey === sdgi
        })

        if (group) {
          group.data_objects.push(fieldDataCopy.toDataObject())
        } else {
          dataGroups.push({
            key: dgi,
            skey: sdgi,
            data_objects: [fieldDataCopy.toDataObject()]
          })
        }
      }
    })
    // Transform data to API compatible structure
    let groupsArray = dataGroups.map(group => {
      return {
        data_group: group.key,
        data_objects: group.data_objects
      }
    })
    return { 'form_data': groupsArray, 'form_files': formFiles }
  }

  inputOnChange (field) {
    for (let key in this.dependencies) {
      if (key.replace(/[0-9]/g, '') === field.id.replace(/[0-9]/g, '')) { // compare UIDs without considering the index
        for (let fieldSet of this.dependencies[key]) {
          fieldSet.checkFieldSetDependencies(this)
        }
      }
    }
  }

  getValueForField (field) {
    return this.fields[field.id].value
  }
}
